/* MatchCard — één match-rij op de Matches pagina.
 * Geëxtraheerd uit pages/dashboard/Matches.jsx (refactor) — bevat ook
 * MatchDocs (pitch-documenten) en ExplainableBullets (feature #13).
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Check, X, Send, Sparkles, Loader2, TrendingUp, TrendingDown, Info,
    Users as UsersIcon, Zap, Presentation, FileText, Eye,
} from "lucide-react";
import { PitchModal } from "@/components/PitchModal";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import { checksTooltip } from "@/components/common/TrustBadge";
import { LinkedInButton } from "@/components/common/LinkedInButton";

export const STATUS_COLORS = {
    proposed: "text-[var(--jr-amber)] bg-[var(--jr-amber)]/10",
    approved: "text-[var(--jr-success)] bg-[var(--jr-success)]/10",
    rejected: "text-[var(--jr-text-3)] bg-white/5",
    outreach_started: "text-[var(--jr-brand)] bg-[var(--jr-brand)]/10",
    meeting: "text-[var(--jr-info)] bg-[var(--jr-info)]/10",
    won: "text-[var(--jr-success)] bg-[var(--jr-success)]/15",
};

/* Pool-badge — "Cronos-first": in één oogopslag zien of de gematchte
 * kandidaat intern (Cronos-bench), extern (ATS) of uit een custom pool komt. */
function PoolBadge({ poolKey, poolMap, matchId }) {
    const { t } = useTranslation();
    if (!poolKey) return null;
    const style = poolKey === "cronos"
        ? "bg-[var(--jr-amber)]/15 text-[var(--jr-amber)] border-[var(--jr-amber)]/40"
        : poolKey === "external"
            ? "bg-white/6 text-[var(--jr-text-2)] border-white/15"
            : "bg-[var(--jr-info)]/12 text-[var(--jr-info)] border-[var(--jr-info)]/35";
    const name = poolMap?.[poolKey] || (poolKey === "cronos" ? "Cronos" : poolKey === "external" ? "ATS / Extern" : poolKey);
    const suffixWord = poolKey === "cronos" ? t("matches.badge_internal")
        : poolKey === "external" ? t("matches.badge_external") : "";
    // Geen dubbelop ("ATS / Extern · extern") wanneer de poolnaam het woord al bevat
    const suffix = suffixWord && !name.toLowerCase().includes(suffixWord.toLowerCase()) ? ` · ${suffixWord}` : "";
    return (
        <span data-testid={`match-pool-badge-${matchId}`}
            title={t("matches.pool_badge_tip", { name })}
            className={`inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border ${style}`}>
            <UsersIcon size={9}/> {name}{suffix}
        </span>
    );
}

export function MatchCard({ m, onAction, diversityActive, poolMap }) {
    const { t } = useTranslation();
    const [showPitch, setShowPitch] = useState(false);
    const statusColor = STATUS_COLORS[m.status] || STATUS_COLORS.proposed;
    const statusLabel = t(`matches.status.${STATUS_COLORS[m.status] ? m.status : "proposed"}`);
    const divInfo = m.__div;
    const divBoost = diversityActive ? (divInfo?.diversity_boost || 0) : 0;
    const shownConfidence = diversityActive && divInfo ? divInfo.diversity_adjusted_confidence : m.confidence;
    const adjSkills = m.adjacent_skills || [];
    const adjBonus = m.adjacency_bonus || 0;
    return (
        <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] hover:border-white/14 transition-colors p-6" data-testid={`match-card-${m.id}`}>
            <div className="grid lg:grid-cols-[1fr_auto_1fr_auto] gap-6 items-center">
                {/* Signal side */}
                <div className="rounded-lg border border-white/10 bg-[var(--jr-surface-2)] p-4">
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)]">{t("matches.signal_side")}</div>
                    <div className="mt-1 flex items-center justify-between">
                        <span className="font-display text-lg text-white">{m.company_name}</span>
                        {m.signal && <span className="text-[10px] font-mono tracking-[0.16em] text-[var(--jr-brand)]">Score {m.signal.score}</span>}
                    </div>
                    {m.signal && (
                        <>
                            <p className="text-xs text-[var(--jr-text-2)] mt-1">{m.signal.industry} · {m.signal.location}</p>
                            <div className="mt-2 inline-block rounded px-2 py-0.5 text-xs bg-[var(--jr-brand)]/10 text-[var(--jr-brand)] border border-[var(--jr-brand)]/30">
                                {m.signal.signal_type}
                            </div>
                            <p className="text-sm text-[var(--jr-text-2)] mt-2 leading-relaxed">{m.signal.signal_title}</p>
                        </>
                    )}
                </div>

                {/* Bridge confidence */}
                <div className="text-center px-2">
                    <div className="relative w-16 h-16 mx-auto">
                        <svg viewBox="0 0 36 36" className="w-full h-full">
                            <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2"/>
                            <circle
                                cx="18" cy="18" r="16" fill="none"
                                stroke="var(--jr-brand)" strokeWidth="2"
                                strokeDasharray={`${shownConfidence}, 100`}
                                strokeLinecap="round"
                                transform="rotate(-90 18 18)"
                            />
                        </svg>
                        <span className="absolute inset-0 grid place-items-center font-display text-sm text-white">{shownConfidence}%</span>
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mt-2">Confidence</div>
                    {/* Sprint 4b · #14 adjacency bonus badge */}
                    {adjBonus > 0 && (
                        <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-mono bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" data-testid={`adj-badge-${m.id}`}
                            title={adjSkills.join(" · ")}>
                            <Zap size={9}/>
                            +{adjBonus} adj
                        </div>
                    )}
                    {/* Sprint 4b · #15 diversity boost badge */}
                    {diversityActive && divBoost > 0 && (
                        <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-mono bg-[#0891b2]/15 text-[#0891b2] border border-[#0891b2]/30" data-testid={`div-badge-${m.id}`}
                            title={(divInfo?.diversity_reasons || []).join(" · ")}>
                            <UsersIcon size={9}/>
                            +{divBoost} div
                        </div>
                    )}
                </div>

                {/* Candidate side */}
                <div className={`rounded-lg border p-4 ${m.talent_pool === "cronos" ? "border-[var(--jr-amber)]/30 bg-[var(--jr-amber)]/[0.04]" : "border-white/10 bg-[var(--jr-surface-2)]"}`}>
                    <div className="flex items-center justify-between gap-2">
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)]">{t("matches.candidate_side")}</div>
                        <div className="flex items-center gap-1.5">
                            {/* Anti-hallucinatie: mini trust-indicator (indien geverifieerd) */}
                            {m.candidate?.verification && (
                                <span data-testid={`match-cand-trust-${m.id}`}
                                    title={`${t("verify.trust")} ${m.candidate.verification.trust_score}/100\n\n${checksTooltip(m.candidate.verification)}`}
                                    className={`inline-flex items-center gap-0.5 text-[9px] font-mono px-1 py-0.5 rounded cursor-help ${
                                        m.candidate.verification.status === "verified" ? "bg-[#22c55e]/12 text-[#22c55e]"
                                        : m.candidate.verification.status === "partial" ? "bg-[var(--jr-amber)]/12 text-[var(--jr-amber)]"
                                        : "bg-[var(--jr-error)]/12 text-[var(--jr-error)]"}`}>
                                    {m.candidate.verification.status === "verified" ? <ShieldCheck size={9}/> : <ShieldAlert size={9}/>}
                                    {m.candidate.verification.trust_score}
                                </span>
                            )}
                            <PoolBadge poolKey={m.talent_pool} poolMap={poolMap} matchId={m.id}/>
                        </div>
                    </div>
                    <div className="mt-1 flex items-center justify-between">
                        <span className="font-display text-lg text-white">{m.candidate_name}</span>
                        <div className="flex items-center gap-2">
                            <LinkedInButton url={m.candidate?.linkedin_url} name={m.candidate_name} variant="icon" className="!w-5 !h-5" testid={`match-linkedin-${m.id}`} resolve={m.candidate_id ? { type: "candidate", id: m.candidate_id } : undefined} />
                            {m.candidate && <span className="text-[10px] font-mono tracking-[0.16em] text-[var(--jr-amber)]">{m.candidate.seniority}</span>}
                        </div>
                    </div>
                    {m.candidate && (
                        <>
                            <p className="text-xs text-[var(--jr-text-2)] mt-1">{m.candidate.title} · {m.candidate.location}</p>
                            <div className="mt-2 flex flex-wrap gap-1">
                                {(m.candidate.skills || []).slice(0, 4).map((s, i) => (
                                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[var(--jr-text-2)]">{s}</span>
                                ))}
                            </div>
                        </>
                    )}
                    {/* Adjacent-skills strip */}
                    {adjSkills.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1" data-testid={`adj-skills-${m.id}`}>
                            {adjSkills.slice(0, 3).map((s, i) => (
                                <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 font-mono" title="Skill-adjacency hit">
                                    {s}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Status + actions */}
                <div className="flex flex-col items-stretch gap-2 min-w-[160px]">
                    <span className={`inline-flex items-center justify-center text-[10px] font-mono uppercase tracking-[0.18em] px-3 py-1 rounded-full ${statusColor}`}>
                        {statusLabel}
                    </span>
                    {m.status === "proposed" && (
                        <>
                            <button onClick={() => onAction(m.id, "approve")} data-testid={`approve-${m.id}`} className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[var(--jr-brand)] text-white text-xs font-medium hover:bg-[var(--jr-brand-2)] transition-colors">
                                <Check size={14}/> {t("common.approve")}
                            </button>
                            <button onClick={() => onAction(m.id, "reject")} data-testid={`reject-${m.id}`} className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-white/10 text-[var(--jr-text-2)] text-xs hover:bg-white/5 transition-colors">
                                <X size={14}/> {t("matches.reject")}
                            </button>
                        </>
                    )}
                    {m.status === "approved" && (
                        <button onClick={() => onAction(m.id, "start_outreach")} data-testid={`outreach-${m.id}`} className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[var(--jr-amber)] text-black text-xs font-medium hover:bg-[var(--jr-amber)]/80 transition-colors">
                            <Send size={14}/> {t("matches.start_outreach")}
                        </button>
                    )}
                    <button onClick={() => setShowPitch(true)} data-testid={`pitch-btn-${m.id}`}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-[#a855f7]/40 bg-[#a855f7]/10 text-[#a855f7] text-xs font-medium hover:bg-[#a855f7]/20 transition-colors">
                        <Presentation size={14}/> {t("pitch.button")}
                    </button>
                </div>
            </div>

            <div className="mt-4 pt-4 border-t border-white/8">
                <ExplainableBullets m={m}/>
                <div className="mt-3 flex items-start gap-2">
                    <Sparkles size={14} className="text-[var(--jr-amber)] mt-0.5 shrink-0"/>
                    <p className="text-xs text-[var(--jr-text-2)] leading-relaxed italic">{m.rationale}</p>
                </div>
                <MatchDocs m={m}/>
            </div>
            {showPitch && <PitchModal match={m} onClose={() => setShowPitch(false)}/>}
        </div>
    );
}

/* Generated pitch documents shown directly on the match card. */
function MatchDocs({ m }) {
    const { t } = useTranslation();
    const pitches = m.pitches || [];
    if (pitches.length === 0) return null;
    const download = async (p, fmt) => {
        try {
            const r = await api.get(`/pitch/${p.id}/file/${fmt}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `match_${(m.candidate_name || "kandidaat").replace(/\s+/g, "_")}.${fmt}`;
            a.click(); URL.revokeObjectURL(url);
        } catch { toast.error(t("pitch.download_failed")); }
    };
    return (
        <div className="mt-3 pt-3 border-t border-white/8 flex items-center gap-2 flex-wrap" data-testid={`match-docs-${m.id}`}>
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{t("pitch.docs_label")}</span>
            {pitches.map((p) => (
                <span key={p.id} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-[var(--jr-surface-2)]/60 pl-2.5 pr-1.5 py-1">
                    <span className="text-[9px] font-mono text-[var(--jr-text-3)]">
                        {new Date(p.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short" })} · {p.language?.toUpperCase()}
                    </span>
                    {p.pdf_path && (
                        <button onClick={() => download(p, "pdf")} data-testid={`match-doc-pdf-${p.id}`}
                            title={t("pitch.fmt_onepager")}
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-[var(--jr-brand)]/12 text-[var(--jr-brand)] border border-[var(--jr-brand)]/30 hover:bg-[var(--jr-brand)]/25">
                            <FileText size={9}/> {t("pitch.label_onepager")}
                        </button>
                    )}
                    {p.pptx_path && (
                        <button onClick={() => download(p, "pptx")} data-testid={`match-doc-pptx-${p.id}`}
                            title={t("pitch.fmt_deck")}
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-[#a855f7]/12 text-[#a855f7] border border-[#a855f7]/30 hover:bg-[#a855f7]/25">
                            <Presentation size={9}/> {t("pitch.label_deck")}
                        </button>
                    )}
                    {p.share_enabled && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] font-mono px-1 py-0.5 rounded bg-[#22c55e]/12 text-[#22c55e]"
                            title={p.last_opened_at ? t("pitch.last_opened", { when: new Date(p.last_opened_at).toLocaleString() }) : t("pitch.not_opened_yet")}
                            data-testid={`match-doc-opens-${p.id}`}>
                            <Eye size={9}/> {p.open_count || 0}×
                        </span>
                    )}
                </span>
            ))}
        </div>
    );
}

function ExplainableBullets({ m }) {
    const { t } = useTranslation();
    const [bullets, setBullets] = useState(m.explain_bullets || []);
    const [loading, setLoading] = useState(false);
    const load = async (force = false) => {
        setLoading(true);
        try {
            const r = await api.post("/matching/explain", { match_id: m.id, force_refresh: force });
            setBullets(r.data?.bullets || []);
        } catch (e) { toast.error(t("matches.explain_failed")); }
        finally { setLoading(false); }
    };
    const hasHistory = (m.score_history || []).length > 0;
    const lastDelta = hasHistory ? m.score_history[m.score_history.length - 1].delta : null;
    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <Info size={12} className="text-[#a855f7]"/>
                    <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{t("matches.why_match")}</span>
                    {lastDelta !== null && lastDelta !== 0 && (
                        <span className={`text-[9px] font-mono px-1.5 py-0 rounded ${lastDelta > 0 ? "bg-[#22c55e]/15 text-[#22c55e]" : "bg-[#ef4444]/15 text-[#ef4444]"}`}
                            title={t("matches.rescores_registered", { n: m.score_history.length })}>
                            {lastDelta > 0 ? <TrendingUp size={8} className="inline"/> : <TrendingDown size={8} className="inline"/>}
                            {" "}{lastDelta > 0 ? "+" : ""}{lastDelta} {t("matches.today")}
                        </span>
                    )}
                </div>
                {bullets.length === 0 && !loading && (
                    <button onClick={() => load(false)} data-testid={`explain-btn-${m.id}`}
                        className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#a855f7] hover:text-white">
                        {t("matches.gen_explain")} →
                    </button>
                )}
                {bullets.length > 0 && !loading && (
                    <button onClick={() => load(true)}
                        className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] hover:text-white">
                        {t("matches.regen_explain")}
                    </button>
                )}
            </div>
            {loading ? (
                <div className="text-[11px] text-[var(--jr-text-3)]"><Loader2 size={11} className="animate-spin inline mr-1"/> {t("common.loading")}</div>
            ) : bullets.length > 0 ? (
                <ul className="space-y-0.5 text-xs">
                    {bullets.map((b, i) => (
                        <li key={i} className="text-[var(--jr-text-2)]">{b}</li>
                    ))}
                </ul>
            ) : (
                <div className="text-[11px] text-[var(--jr-text-3)] italic">{t("matches.no_explain")}</div>
            )}
        </div>
    );
}
